import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/features/auth/AuthProvider'
import { LanguageProvider } from '@/i18n/LanguageProvider'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { ConfirmProvider } from '@/components/dialog/ConfirmProvider'
import { useAuth } from '@/features/auth/useAuth'
import { AppShell } from '@/components/layout/AppShell'
import { IncomingCallListener } from '@/features/call/IncomingCallListener'
import { UpdatePrompt } from '@/components/layout/UpdatePrompt'
import { Spinner } from '@/components/ui'

const SignIn = lazy(() => import('@/pages/SignIn'))
const AuthCallback = lazy(() => import('@/pages/AuthCallback'))
const AuthConfirm = lazy(() => import('@/pages/AuthConfirm'))
const NewPassword = lazy(() => import('@/pages/NewPassword'))
const Home = lazy(() => import('@/pages/Home'))
const PostDetail = lazy(() => import('@/pages/PostDetail'))
const Chats = lazy(() => import('@/pages/Chats'))
const Conversation = lazy(() => import('@/pages/Conversation'))
const ConversationInfo = lazy(() => import('@/pages/ConversationInfo'))
const ConversationMedia = lazy(() => import('@/pages/ConversationMedia'))
const NewGroup = lazy(() => import('@/pages/NewGroup'))
const People = lazy(() => import('@/pages/People'))
const Profile = lazy(() => import('@/pages/Profile'))
const Settings = lazy(() => import('@/pages/Settings'))
const CallRoom = lazy(() => import('@/pages/CallRoom'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Realtime pushes the fresh data; polling on top of it just burns
      // battery and rate limit.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 2,
    },
  },
})

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner className="text-muted size-6" />
    </div>
  )
}

function RequireAuth() {
  const { session, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  if (!session) return <Navigate to="/sign-in" replace />
  return <Outlet />
}

function RedirectIfAuthed() {
  const { session, loading } = useAuth()
  if (loading) return <FullPageSpinner />
  return session ? <Navigate to="/" replace /> : <Outlet />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <AuthProvider>
                <Suspense fallback={<FullPageSpinner />}>
                  <Routes>
                    <Route element={<RedirectIfAuthed />}>
                      <Route path="/sign-in" element={<SignIn />} />
                    </Route>
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    {/* Email links land here; works from any device, unlike PKCE. */}
                    <Route path="/auth/confirm" element={<AuthConfirm />} />
                    {/* Outside RequireAuth: a recovery link creates the session itself. */}
                    <Route path="/auth/new-password" element={<NewPassword />} />

                    <Route element={<RequireAuth />}>
                      {/* The call screen is full-bleed: no tab bar, no chrome. */}
                      <Route path="/call/:callId" element={<CallRoom />} />

                      <Route element={<AppShell />}>
                        <Route index element={<Home />} />
                        {/* The service worker sends notification taps here. */}
                        <Route path="/post/:postId" element={<PostDetail />} />
                        <Route path="/chats" element={<Chats />} />
                        <Route path="/chats/new-group" element={<NewGroup />} />
                        <Route path="/chats/:conversationId" element={<Conversation />} />
                        <Route
                          path="/chats/:conversationId/info"
                          element={<ConversationInfo />}
                        />
                        <Route
                          path="/chats/:conversationId/media"
                          element={<ConversationMedia />}
                        />
                        <Route path="/people" element={<People />} />
                        <Route path="/u/:userId" element={<Profile />} />
                        <Route path="/settings" element={<Settings />} />
                      </Route>
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
                <IncomingCallListener />
                <UpdatePrompt />
              </AuthProvider>
            </BrowserRouter>
          </ConfirmProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
